/**
 * Composable for handling MCP server redirects and OAuth callbacks
 */

interface McpRedirectOptions {
  allowLocalhost?: boolean
  trustedDomains?: string[]
}

export const useMcpRedirect = (options: McpRedirectOptions = {}) => {
  const config = useRuntimeConfig()
  
  // Default trusted MCP server domains
  const defaultTrustedDomains = [
    'mcp.superseeded.ai',
    'superseeded-mcp-server-auth.edanweis.workers.dev', // Production MCP server
    'localhost:8788', // Local development MCP server
    'localhost:6274', // Alternative local MCP server port
  ]
  
  const trustedDomains = options.trustedDomains || defaultTrustedDomains
  const allowLocalhost = options.allowLocalhost ?? true

  /**
   * Check if a URL is a valid MCP server callback URL
   */
  const isMcpServerUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url)
      
      // Check if it's a trusted domain
      const hostWithPort = urlObj.port ? `${urlObj.hostname}:${urlObj.port}` : urlObj.hostname
      
      // Check against trusted domains
      const isTrusted = trustedDomains.some(domain => {
        return hostWithPort === domain || urlObj.hostname === domain
      })
      
      if (!isTrusted) {
        return false
      }
      
      // Additional localhost validation in production
      if (!allowLocalhost && urlObj.hostname === 'localhost' && process.env.NODE_ENV === 'production') {
        return false
      }
      
      // Check for common MCP callback patterns
      const isMcpCallback = urlObj.pathname.includes('/callback') || 
                           urlObj.pathname.includes('/oauth') ||
                           urlObj.searchParams.has('code') ||
                           urlObj.searchParams.has('state')
      
      return isMcpCallback
    } catch (error) {
      console.warn('Invalid URL provided to isMcpServerUrl:', url)
      return false
    }
  }

  /**
   * Validate and sanitize redirect URL for security
   */
  const validateRedirectUrl = (url: string): { isValid: boolean; sanitizedUrl?: string; reason?: string } => {
    if (!url) {
      return { isValid: false, reason: 'URL is required' }
    }

    try {
      const urlObj = new URL(url)
      
      // Check protocol - only allow https in production, http for localhost in dev
      if (urlObj.protocol !== 'https:' && urlObj.protocol !== 'http:') {
        return { isValid: false, reason: 'Only HTTP and HTTPS protocols are allowed' }
      }
      
      // In production, only allow https except for localhost
      if (process.env.NODE_ENV === 'production' && 
          urlObj.protocol !== 'https:' && 
          urlObj.hostname !== 'localhost') {
        return { isValid: false, reason: 'HTTPS required in production' }
      }
      
      // Check if it's a trusted MCP server
      if (!isMcpServerUrl(url)) {
        return { isValid: false, reason: 'URL is not from a trusted MCP server' }
      }
      
      return { 
        isValid: true, 
        sanitizedUrl: urlObj.toString() 
      }
    } catch (error) {
      return { isValid: false, reason: 'Invalid URL format' }
    }
  }

  /**
   * Check if user should be allowed to redirect to MCP server even if on waitlist
   */
  const shouldAllowMcpRedirect = (redirectUrl?: string): boolean => {
    if (!redirectUrl) {
      console.log('[DEBUG] shouldAllowMcpRedirect: No redirectUrl provided')
      return false
    }

    const validation = validateRedirectUrl(redirectUrl)
    console.log('[DEBUG] shouldAllowMcpRedirect:', redirectUrl, 'isValid:', validation.isValid, 'reason:', validation.reason)
    return validation.isValid
  }

  /**
   * Build MCP-aware navigation URL preserving OAuth state
   */
  const buildRedirectUrl = (baseUrl: string, params: Record<string, string> = {}): string => {
    try {
      const url = new URL(baseUrl)
      
      // Preserve existing query parameters and add new ones
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value)
        }
      })
      
      return url.toString()
    } catch (error) {
      console.error('Error building redirect URL:', error)
      return baseUrl
    }
  }

  /**
   * Build redirect URL for MCP servers using Nuxt Supabase module's OAuth flow
   */
  const buildMcpRedirectUrl = async (
    baseUrl: string,
    options: {
      preserveParams?: boolean,
      preserveState?: boolean,
      extraParams?: Record<string, string>
      useSupabaseOAuth?: boolean
    } = {}
  ): Promise<string> => {
    try {
      const supabase = useSupabaseClient()
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        console.warn('No access token available for MCP redirect')
        return baseUrl
      }

      const url = new URL(baseUrl)

      // Always use the Nuxt Supabase module's OAuth flow for MCP servers
      // This ensures proper PKCE flow and session management
      const loginUrl = new URL('/login', window.location.origin)

      // Set up OAuth parameters for MCP server
      const oauthParams = {
        redirect_to: baseUrl,
        state: options.extraParams?.state || '',
        response_type: 'code',
        client_id: 'mcp-client', // Identifier for MCP clients
      }

      Object.entries(oauthParams).forEach(([key, value]) => {
        if (value) loginUrl.searchParams.set(key, value)
      })

      return loginUrl.toString()
    } catch (error) {
      console.error('Error building MCP redirect URL:', error)
      return baseUrl
    }
  }

  /**
   * Extract OAuth state from redirect URL for logging/debugging
   */
  const extractOAuthState = (url: string): string | null => {
    try {
      const urlObj = new URL(url)
      return urlObj.searchParams.get('state')
    } catch (error) {
      return null
    }
  }

  return {
    isMcpServerUrl,
    validateRedirectUrl,
    shouldAllowMcpRedirect,
    buildRedirectUrl,
    buildMcpRedirectUrl,
    extractOAuthState,
    trustedDomains
  }
}